<div
  class="message-container flex items-start px-3 sm:px-4 md:px-6 lg:px-8 xl:px-16 pt-3 pb-3 animate-message-in"
  [ngClass]="{'bg-gray-50/50 dark:bg-dark-user-bubble': !chatMessage.bot}">

  <!-- FIN-0106: Circle-First Design - full circle avatars -->
  <img
    *ngIf="!isDataUrl(safeImageUrl)"
    [ngSrc]="safeImageUrl"
    (error)="onImageError()"
    alt="Profile picture"
    height="32"
    width="32"
    class="rounded-avatar flex-shrink-0"
    style="min-width: 32px;"
    [priority]="chatMessage.bot && isFirstBotMessage"
  />
  <img
    *ngIf="isDataUrl(safeImageUrl)"
    [src]="safeImageUrl"
    (error)="onImageError()"
    alt="Profile picture"
    height="32"
    width="32"
    class="rounded-avatar flex-shrink-0"
    style="min-width: 32px;"
  />

  <!-- Message content container with loading states -->
  <div class="flex-1 pl-2">
    <!-- Progress Portal - Shows only for operations with actual progress tracking -->
    <div *ngIf="chatMessage.bot && isLastBotMessage && shouldShowProgressPortal() && (isLoading || demoPortal)"
         class="progress-portal-container">
      <div class="progress-messages-flow">
        <!-- Show active progress messages if available -->
        <div *ngIf="activeProgressMessages.length > 0">
          <div *ngFor="let msg of activeProgressMessages; trackBy: trackByMessageId"
               class="progress-message-item"
               [@messageAnimation]>
            <div class="flex items-center gap-2">
              <div class="progress-dot"></div>
              <span class="progress-text">{{ msg.message }}</span>
            </div>
          </div>
        </div>
        <!-- FIN-0104: Loading dots use subtle gray (not brand) to reduce visual noise -->
        <div *ngIf="activeProgressMessages.length === 0" class="progress-message-item">
          <div class="flex items-center gap-2">
            <div class="flex items-center space-x-1.5">
              <div class="h-2 w-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-pulse"></div>
              <div class="h-2 w-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-pulse delay-75"></div>
              <div class="h-2 w-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-pulse delay-150"></div>
            </div>
            <span class="progress-text ml-2">{{ getLoadingMessage() }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Simplified progress trace for completed messages (improved UX) -->
    <!--
      Added **isLastBotMessage** condition so the thought-trace
      (and its "Thought for . seconds" header) is rendered **only**
      for the most-recent bot reply.
    -->
    <div *ngIf="chatMessage.bot && isLastBotMessage && !isLoading && showProgressTrace && progressHistory.length > 1 && isTraceUIVisible()"
         class="thought-trace-container relative my-1 min-h-6">
      <!-- Default compact view -->
      <div *ngIf="!isProgressExpanded" class="relative flex max-w-full origin-top-left flex-col gap-2 overflow-x-clip">
        <span data-state="closed">
          <button class="relative w-full text-start" (click)="toggleProgressExpanded()">
            <div class="flex w-full items-center justify-between text-start">
              <div class="flex items-center gap-1">
                <!-- LOADING  -->
                <ng-container *ngIf="isLoading">
                  <!-- No specific phase yet -->
                  <span *ngIf="!currentPhase" class="animate-pulse">
                    Thinking... ({{processingTimeSeconds > 0 ? processingTimeSeconds : '...'}} s)
                  </span>
                  <!-- Specific phase while loading -->
                  <div *ngIf="currentPhase" class="flex items-center gap-1">
                    <svg class="animate-spin w-4 h-4">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-opacity="0.25" stroke-width="2" fill="none" />
                      <path d="M 8 1 A 7 7 0 0 1 15 8" stroke="currentColor" stroke-width="2" fill="none" />
                    </svg>
                    <span>{{ currentPhase.message }}</span>
                    <span *ngIf="currentPhase.elapsedMs" class="text-xs text-gray-400 ml-1">
                      ({{ currentPhase.elapsedMs }} ms)
                    </span>
                  </div>
                </ng-container>
                <!-- FINISHED  -->
                <span *ngIf="!isLoading" class="text-token-text-secondary">
                  Thought for {{ processingTimeSeconds }} {{ processingTimeSeconds === 1 ? 'second' : 'seconds' }}
                </span>

                <!-- Expand/collapse arrow -->
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                     class="icon-sm text-token-text-secondary inline align-middle transition-transform"
                     [ngClass]="{'rotate-90': isProgressExpanded, 'rotate-0': !isProgressExpanded}">
                  <path fill-rule="evenodd" clip-rule="evenodd"
                        d="M9.29289 18.7071C8.90237 18.3166 8.90237 17.6834 9.29289 17.2929L14.5858 12L9.29289 6.70711C8.90237 6.31658 8.90237 5.68342 9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289L16.7071 11.2929C16.8946 11.4804 17 11.7348 17 12C17 12.2652 16.8946 12.5196 16.7071 12.7071L10.7071 18.7071C10.3166 19.0976 9.68342 19.0976 9.29289 18.7071Z"
                        fill="currentColor"></path>
                </svg>
              </div>
            </div>
          </button>
        </span>
      </div>

      <!-- Expanded view for details -->
      <div *ngIf="isProgressExpanded" class="relative flex max-w-full origin-top-left flex-col gap-2 overflow-x-clip">
        <!-- Header with collapse option -->
        <span data-state="open">
          <button class="relative w-full text-start" (click)="toggleProgressExpanded()">
            <div class="flex w-full items-center justify-between text-start">
              <div class="flex items-center gap-1">
                <span class="flex items-center gap-1 align-middle text-token-text-secondary hover:text-token-text-primary">
                  Thought for {{ processingTimeSeconds > 0 ? processingTimeSeconds : '...' }} {{ processingTimeSeconds === 1 ? 'second' : 'seconds' }}
                </span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                     class="icon-sm text-token-text-secondary inline align-middle transition-transform rotate-90">
                  <path fill-rule="evenodd" clip-rule="evenodd"
                        d="M9.29289 18.7071C8.90237 18.3166 8.90237 17.6834 9.29289 17.2929L14.5858 12L9.29289 6.70711C8.90237 6.31658 8.90237 5.68342 9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289L16.7071 11.2929C16.8946 11.4804 17 11.7348 17 12C17 12.2652 16.8946 12.5196 16.7071 12.7071L10.7071 18.7071C10.3166 19.0976 9.68342 19.0976 9.29289 18.7071Z"
                        fill="currentColor"></path>
                </svg>
              </div>
            </div>
          </button>
        </span>

        <!-- Detailed progress history (only shown when expanded) -->
        <div class="thought-content relative z-0">
          <div class="relative flex h-full flex-col" style="gap: 12px; margin: 4px 0px;">
            <!-- Skip "Operation completed" messages in the history display -->
            <div *ngFor="let thought of progressHistory; let i = index; let isLast = last"
                 class="text-token-text-secondary start-0 end-0 top-0 flex origin-left"
                 [ngClass]="{'hidden': thought.message === 'Operation completed'}">
              <div class="flex w-full items-start gap-2 overflow-clip">
                <div class="flex h-full w-4 shrink-0 flex-col items-center gap-2">
                  <div class="flex h-5 shrink-0 items-center justify-center">
                    <!-- Regular step dot -->
                    <div *ngIf="thought.message !== 'Drafting reply...'"
                         class="h-[6px] w-[6px] rounded-full thought-dot"></div>
                    <!-- Show checkmark for final step -->
                    <svg *ngIf="thought.message === 'Drafting reply...'"
                         width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                         class="h-[15px] w-[15px] thought-check">
                      <path fill-rule="evenodd" clip-rule="evenodd"
                            d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM16.0755 7.93219C16.5272 8.25003 16.6356 8.87383 16.3178 9.32549L11.5678 16.0755C11.3931 16.3237 11.1152 16.4792 10.8123 16.4981C10.5093 16.517 10.2142 16.3973 10.0101 16.1727L7.51006 13.4227C7.13855 13.014 7.16867 12.3816 7.57733 12.0101C7.98598 11.6386 8.61843 11.6687 8.98994 12.0773L10.6504 13.9039L14.6822 8.17451C15 7.72284 15.6238 7.61436 16.0755 7.93219Z"
                            fill="currentColor"></path>
                    </svg>
                  </div>
                  <!-- Connecting line between steps (not for the last item) -->
                  <div *ngIf="!isLast"
                       class="thought-line h-full w-[1px] rounded-full"></div>
                </div>
                <div class="thought-message text-sm w-full break-words">
                  <p>{{ thought.message }} <span *ngIf="thought.timestamp" class="text-xs text-gray-400 ml-1">({{ getElapsedTimeBetweenSteps(thought, i) }})</span></p>
                </div>
              </div>
            </div>

            <!-- Only show Done indicator when there's no "Drafting reply" or "Operation completed" message -->
            <div *ngIf="!isLoading && !hasOperationCompletedMessage()" class="text-token-text-secondary start-0 end-0 top-0 flex origin-left">
              <div class="flex h-full w-4 shrink-0 flex-col items-center gap-2">
                <div class="flex h-5 shrink-0 items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                       class="h-[15px] w-[15px]">
                    <path fill-rule="evenodd" clip-rule="evenodd"
                          d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM16.0755 7.93219C16.5272 8.25003 16.6356 8.87383 16.3178 9.32549L11.5678 16.0755C11.3931 16.3237 11.1152 16.4792 10.8123 16.4981C10.5093 16.517 10.2142 16.3973 10.0101 16.1727L7.51006 13.4227C7.13855 13.014 7.16867 12.3816 7.57733 12.0101C7.98598 11.6386 8.61843 11.6687 8.98994 12.0773L10.6504 13.9039L14.6822 8.17451C15 7.72284 15.6238 7.61436 16.0755 7.93219Z"
                          fill="currentColor"></path>
                  </svg>
                </div>
              </div>
              <div class="text-token-text-secondary text-sm">Done</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FIN-0104: Loading indicator uses subtle gray (not brand) to reduce visual noise -->
    <div *ngIf="isLoading && !shouldShowProgressPortal()" class="py-4 px-3 flex items-center justify-start">
      <div class="flex items-center space-x-1.5">
        <div class="h-1.5 w-1.5 rounded-full bg-gray-400 dark:bg-gray-500 animate-pulse"></div>
        <div class="h-1.5 w-1.5 rounded-full bg-gray-400 dark:bg-gray-500 animate-pulse delay-75"></div>
        <div class="h-1.5 w-1.5 rounded-full bg-gray-400 dark:bg-gray-500 animate-pulse delay-150"></div>
      </div>
      <span class="ml-3 text-sm text-gray-500 dark:text-gray-400">{{ getLoadingMessage() || 'Thinking...' }}</span>
    </div>

    <!-- Actual message content when available -->
    <div *ngIf="!isLoading && typewriterText" id="message_text" class="pr-5 min-h-[60px] pt-4 pb-4"
         [innerHTML]="formatStringToHtml(typewriterText)">
    </div>

    <!-- Response time indicator - only shows for completed messages -->
    <div *ngIf="hasResponseTime() && !showProgressTrace"
         class="text-xs text-gray-400 dark:text-gray-500 mt-1 mb-2 text-right pr-5">
      Answered in {{formatGenerationTime(responseTime!)}}
    </div>

    <!-- Enhanced Jaeger trace debugging - only shows in development mode when trace context is available and message has content -->
    <div *ngIf="isDevelopmentMode && hasTraceContext() && !isLoading && hasActualContent() && isTraceUIVisible()"
         class="mt-2 mb-2 pr-5">

      <!-- Main trace info with expand button -->
      <div class="flex items-center gap-2 text-xs text-gray-500">
        <svg class="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span class="font-medium text-gray-600">OpenTelemetry Trace:</span>

        <!-- Main trace ID link -->
        <a [href]="getJaegerTraceUrl(chatMessage.tracingContext!.traceId!)"
           target="_blank"
           rel="noopener noreferrer"
           class="text-brand-600 hover:text-brand-800 underline hover:no-underline transition-colors">
          {{chatMessage.tracingContext!.traceId!.substring(0, 16)}}...
        </a>

        <!-- Copy main trace ID button -->
        <button (click)="copyTraceId(chatMessage.tracingContext!.traceId!)"
                class="ml-1 p-1 rounded hover:bg-gray-100 transition-colors"
                title="Copy main trace ID">
          <svg class="w-3 h-3 text-gray-500 hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
          </svg>
        </button>

        <!-- Chunk debugging toggle -->
        <button (click)="toggleChunkTracing()"
                class="ml-2 px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-colors"
                title="Toggle chunk-level trace debugging">
          🔍 Debug Chunks ({{getChunkCount()}})
        </button>
      </div>

      <!-- Expandable chunk-level tracing details -->
      <div *ngIf="showChunkTracing"
           class="mt-3 p-3 bg-gray-50 rounded border-l-4 border-orange-400">
        <div class="text-xs font-semibold text-gray-700 mb-2">
          📊 Chunk Analysis for Duplicate Detection
        </div>

        <!-- Chunk statistics -->
        <div class="mb-3 text-xs text-gray-600">
          <span class="font-medium">Total Chunks:</span> {{getChunkCount()}} |
          <span class="font-medium">Unique Sequences:</span> {{getUniqueSequenceCount()}} |
          <span class="font-medium">Duplicates Detected:</span>
          <span [class]="getDuplicateCount() > 0 ? 'text-red-600 font-bold' : 'text-green-600'">
            {{getDuplicateCount()}}
          </span>
        </div>

        <!-- Chunk details table -->
        <div class="overflow-x-auto">
          <table class="min-w-full text-xs">
            <thead class="bg-gray-100">
              <tr>
                <th class="px-2 py-1 text-left">Seq</th>
                <th class="px-2 py-1 text-left">Content Preview</th>
                <th class="px-2 py-1 text-left">Received</th>
                <th class="px-2 py-1 text-left">Trace ID</th>
                <th class="px-2 py-1 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let chunk of getChunkTraceInfo(); trackBy: trackChunkById"
                  [class]="chunk.isDuplicate ? 'bg-red-50 border-l-2 border-red-400' : 'hover:bg-gray-50'">
                <td class="px-2 py-1 font-mono">
                  <span [class]="chunk.isDuplicate ? 'text-red-600 font-bold' : ''">
                    {{chunk.sequenceNumber}}
                  </span>
                </td>
                <td class="px-2 py-1 max-w-32 truncate" [title]="chunk.content">
                  {{chunk.content}}
                </td>
                <td class="px-2 py-1 font-mono text-gray-500">
                  {{chunk.receivedTime | date:'HH:mm:ss.SSS'}}
                </td>
                <td class="px-2 py-1">
                  <a *ngIf="chunk.traceId"
                     [href]="getJaegerTraceUrl(chunk.traceId)"
                     target="_blank"
                     rel="noopener noreferrer"
                     class="text-brand-600 hover:text-brand-800 underline text-xs font-mono"
                     [title]="chunk.traceId">
                    {{chunk.traceId.substring(0, 8)}}...
                  </a>
                  <span *ngIf="!chunk.traceId" class="text-gray-400">N/A</span>
                </td>
                <td class="px-2 py-1">
                  <span *ngIf="chunk.isDuplicate" class="text-red-600 font-bold">DUPLICATE</span>
                  <span *ngIf="!chunk.isDuplicate" class="text-green-600">OK</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Duplicate analysis summary -->
        <div *ngIf="getDuplicateCount() > 0" class="mt-3 p-2 bg-red-50 border border-red-200 rounded">
          <div class="text-xs text-red-800">
            <strong>⚠️ Duplicate Detection:</strong><br>
            Found {{getDuplicateCount()}} duplicate chunks. Check trace IDs to identify source of duplication in backend pipeline.
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
